import { createContext } from 'react'
import type {
  PublicUserWithAccess,
  SessionInfo,
  TwoFactorEmailResponse,
  TwoFactorMethod,
  UserEntity,
} from '@/types/api'

/** Credentials kept client-side while a second-factor challenge is completed. */
export type PendingSignIn = { email: string; password: string } | { credential: string }

/** Result of a credential sign-in: either a session, or a pending 2FA challenge. */
export interface SignInOutcome {
  /** The signed-in profile when a session was stored. */
  user?: PublicUserWithAccess
  /** True when a second-factor code is still required; no session was stored. */
  twoFactorRequired: boolean
}

export interface AuthContextValue {
  /** The signed-in user with permissions + groups, or null when signed out. */
  user: PublicUserWithAccess | null
  /** Effective permission codes granted by the user's groups. */
  permissions: string[]
  /** Names of the groups the user belongs to. */
  groups: string[]
  isAuthenticated: boolean
  /** True while the initial session check is in flight. */
  isLoading: boolean
  /** True when the API is unreachable, so the UI can show a retry affordance. */
  isError: boolean
  hasPermission: (code: string) => boolean
  hasAnyPermission: (codes: readonly string[]) => boolean
  /** Coarse entity check (`ADMIN` / `STAFF`). Prefer `hasPermission` for features. */
  hasEntity: (entity: UserEntity | UserEntity[]) => boolean
  signIn: (email: string, password: string) => Promise<SignInOutcome>
  /** Exchange a Google Identity Services credential for an AIMS session. */
  signInWithGoogle: (credential: string) => Promise<SignInOutcome>
  /** Finish a second-factor challenge and store the resulting session. */
  completeTwoFactor: (input: {
    credentials: PendingSignIn
    code: string
    method: TwoFactorMethod
  }) => Promise<PublicUserWithAccess>
  /** Email a fallback second-factor code for the pending credentials. */
  requestTwoFactorEmail: (credentials: PendingSignIn) => Promise<TwoFactorEmailResponse>
  /** Store a session minted elsewhere (sign-up / OTP verification) and load the profile. */
  adoptSession: (session: SessionInfo) => Promise<PublicUserWithAccess>
  /** Clear the session. Pass a notice to explain why the sign-in page appeared. */
  signOut: (options?: { notice?: string }) => Promise<void>
  /** Re-fetch the profile + permissions from the API. */
  reload: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)
